import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { softwareAppSchema, faqSchema } from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function AIAssessment() {
  const faqs = [
    { question: "How does the AI Assessment tool work?", answer: "CheckScript AI uses advanced computer vision (OCR) to read the content on your screen. When you trigger a hotkey, it captures the designated area, processes the text or code, and sends it to our high-performance AI backend (or local Neural Driver) to generate an accurate solution instantly." },
    { question: "Is the AI capable of solving coding questions?", answer: "Yes! CheckScript AI supports over 15 programming languages. It can analyze code snippets, identify bugs, and suggest optimal solutions. We are also rolling out an auto-typing feature that intelligently inputs code with human-like pacing." },
    { question: "What hotkeys are available?", answer: "We provide customizable hotkeys for seamless operation. Defaults include Alt+O for quick scan, Alt+1 through 4 for specific option targeting, and Alt+T for text processing." },
    { question: "Can it solve complex math or diagram-based questions?", answer: "CheckScript is equipped with vision-capable AI models that can interpret charts, diagrams, and complex mathematical equations alongside standard text-based multiple-choice questions." },
    { question: "How fast are the responses?", answer: "Our cloud backend hosted on Enterprise-grade routing delivers answers in milliseconds. If you use the local Neural Driver, latency is essentially zero as the inference happens directly on your GPU." }
  ];

  return (
    <SEOPageLayout>
      <SEO
        title="AI-Powered Assessment Assistant — Solve MCQs & Code Problems | CheckScript"
        description="Master assessments with CheckScript's AI Assistant. Solve MCQs, debug code, and analyze diagrams instantly using seamless hotkeys and advanced OCR."
        keywords="AI MCQ solver, AI assessment assistant, AI exam helper, automated exam assistant, CheckScript AI"
        path="/features/ai-assessment"
        structuredData={{
          ...softwareAppSchema(),
          ...faqSchema(faqs)
        }}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">🧠 Advanced stealth capture system AI</div>
          <h1 className="seo-h1">
            AI-Powered <span className="seo-gradient-text">Assessment Assistant</span>
          </h1>
          <p className="seo-lead">
            Accelerate your learning and problem-solving. CheckScript AI seamlessly integrates with your workflow, providing instant, highly accurate answers to multiple-choice questions and complex coding challenges.
          </p>
          <div className="seo-hero-actions">
            <Link to="/download" className="seo-btn seo-btn--primary">Start Free Trial</Link>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-section-header">
            <h2 className="seo-h2">How It Works in 3 Simple Steps</h2>
            <p className="seo-text-secondary">A frictionless experience designed to keep you focused on the task at hand.</p>
          </div>
          
          <div className="seo-bento-grid">
            <div className="seo-bento-box seo-bento-square">
              <div className="seo-card-icon seo-card-icon--cyan">1</div>
              <h3>Activate Hotkey</h3>
              <p>Press a simple hotkey combination (like Alt+O). CheckScript instantly freezes the screen area containing your question without interrupting your active window.</p>
            </div>
            <div className="seo-bento-box seo-bento-square">
              <div className="seo-card-icon seo-card-icon--emerald">2</div>
              <h3>AI Processing</h3>
              <p>Our advanced OCR engines extract the text, code, or diagram. The data is securely processed by our AI models to determine the optimal solution.</p>
            </div>
            <div className="seo-bento-box seo-bento-square">
              <div className="seo-card-icon seo-card-icon--amber">3</div>
              <h3>Instant Solution</h3>
              <p>The correct answer is delivered back to you in milliseconds, often highlighted directly on your screen or presented in a discreet overlay.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Master Control Hotkeys</h2>
          <div className="seo-table-wrap">
            <table className="seo-table">
              <thead>
                <tr>
                  <th>Hotkey</th>
                  <th>Function</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>Alt + O</code></td>
                  <td>Quick Scan</td>
                  <td>Scans the active region for MCQs and returns the most likely answer.</td>
                </tr>
                <tr>
                  <td><code>Alt + 1-4</code></td>
                  <td>Option Targeting</td>
                  <td>Specifically evaluates option A, B, C, or D against the prompt.</td>
                </tr>
                <tr>
                  <td><code>Alt + T</code></td>
                  <td>Text/Code Analysis</td>
                  <td>Extracts and analyzes longer text passages or code blocks.</td>
                </tr>
                <tr>
                  <td><code>Alt + Y</code></td>
                  <td>Auto-Typing</td>
                  <td>(Premium) Automatically types the generated code solution.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Frequently Asked Questions</h2>
          <ul className="seo-faq-list">
            {faqs.map((faq, i) => (
              <li key={i} className="seo-faq-item">
                <div className="seo-faq-question" style={{ cursor: 'default' }}>
                  {faq.question}
                </div>
                <div className="seo-faq-answer" style={{ maxHeight: '1000px' }}>
                  <div className="seo-faq-answer-inner">{faq.answer}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Experience the Power of Advanced stealth capture system</h2>
            <p className="seo-text-secondary seo-mb-32">Download CheckScript v19.0.0 today and revolutionize how you approach assessments and coding challenges.</p>
            <Link to="/download" className="seo-btn seo-btn--primary">Download Now</Link>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
